'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import { createClient } from '@/lib/supabase/client';

type AuthMode = 'login' | 'signup';

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [loginBgUrl, setLoginBgUrl] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'login_bg_image')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) setLoginBgUrl(data.value);
      });
  }, []);

  return (
    <div
      className="min-h-screen flex relative"
      style={
        loginBgUrl
          ? { backgroundImage: `url(${loginBgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : { backgroundColor: 'var(--color-background)' }
      }
    >
      {/* Full-screen overlay when bg image is set */}
      {loginBgUrl && (
        <div className="absolute inset-0 bg-black/60 pointer-events-none z-0" />
      )}

      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] flex-shrink-0 border-r border-white/10 p-10 relative z-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-80 h-80 bg-primary/8 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <Link href="/" className="flex flex-col items-center gap-2 mb-16">
            <AppLogo size={108} />
            <AppLogo variant="text" size={108} />
          </Link>
          <h2 className={`text-4xl font-extrabold leading-tight mb-4 ${loginBgUrl ? 'text-white' : 'text-foreground'}`}>
            Try Before
            <br />
            <span className="text-gradient">You Ink.</span>
          </h2>
          <p className={`text-base leading-relaxed ${loginBgUrl ? 'text-white/80' : 'text-muted-foreground'}`}>
            Your private tattoo preview space. Upload a photo, place any design, and see it on your
            body before you commit.
          </p>
        </div>

        <div className="relative z-10 space-y-4">
          {[
            { id: 'brand-priv', icon: '🔒', text: 'End-to-end encrypted private space' },
            { id: 'brand-artists', icon: '🎨', text: '840+ verified artists to discover' },
            { id: 'brand-free', icon: '✨', text: 'Free to try — no credit card needed' },
          ].map((item) => (
            <div key={item.id} className={`flex items-center gap-3 text-sm ${loginBgUrl ? 'text-white/80' : 'text-muted-foreground'}`}>
              <span>{item.icon}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative z-10">
        <div className={`w-full max-w-md ${loginBgUrl ? 'bg-background/90 backdrop-blur-sm rounded-2xl p-8 shadow-2xl' : ''}`}>
          {/* Mobile logo */}
          <Link href="/" className="flex lg:hidden flex-col items-center gap-2 justify-center mb-8">
            <AppLogo size={96} />
            <AppLogo variant="text" size={96} />
          </Link>

          {/* Mode tabs */}
          <div className="flex bg-secondary rounded-xl p-1 mb-8">
            {(['login', 'signup'] as AuthMode[]).map((m) => (
              <button
                key={`auth-tab-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 ${
                  mode === m
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === 'login' ? 'Log In' : 'Sign Up'}
              </button>
            ))}
          </div>

          {mode === 'login' ? (
            <LoginForm onSwitchToSignup={() => setMode('signup')} />
          ) : (
            <SignUpForm onSwitchToLogin={() => setMode('login')} />
          )}
        </div>
      </div>
    </div>
  );
}